import { WEGO_JOINTS } from './wegoJoints';
import type { SoundEffect } from '../config/soundEffects';

export type Keyframe = { frame: number; value: number };
export type JointTrack = { id: string; label: string; color: string; keyframes: Keyframe[] };
export type MoveTrackId = 'x' | 'y' | 'z' | 'yaw' | 'bodyRotationX' | 'bodyRotationY' | 'bodyRotationZ';
export type MoveTrack = { id: MoveTrackId; label: string; unit: string; color: string; keyframes: Keyframe[] };
export type EyeLedKeyframe = { frame: number; color: string };
export type SoundKeyframe = { frame: number; sound: SoundEffect };

export type TimelineData = {
  name: string;
  fps: number;
  totalFrames: number;
  joints: JointTrack[];
  movement: MoveTrack[];
  eyeLedKeyframes: EyeLedKeyframe[];
  soundKeyframes?: SoundKeyframe[];
  nativeWalking?: boolean;
  nativeAudioCues?: MotionAudioCue[];
};

export type WegoMotionFrame = {
  durationMs: number;
  jointsDegrees: Record<string, number>;
  jointSpeeds?: Record<string, number>;
  movement?: Partial<Record<MoveTrackId, number>>;
  led?: { r?: number; g?: number; b?: number } | null;
};

export type WegoMotion = {
  motionNo: number;
  name: string;
  frames: WegoMotionFrame[];
  nativePlayback?: {
    sourceFile: string;
    sourceDurationMs: number;
    timing: 'scaled-to-existing-duration';
    profile: string;
    ledCues: { timeMs: number; led: { r: number; g: number; b: number } }[];
    audioCues: MotionAudioCue[];
  };
};

export type MotionAudioCue = { timeMs: number; kind: 'voice' | 'sound'; id: number; volume: number };

export const getMotionAudioCues = (motion: WegoMotion): MotionAudioCue[] => (
  (motion.nativePlayback?.audioCues ?? []).filter(cue => (
    ['voice', 'sound'].includes(cue.kind) && Number.isInteger(cue.id) && cue.id > 0
    && Number.isFinite(cue.timeMs) && cue.timeMs >= 0 && cue.timeMs < getMotionDurationMs(motion)
  )).sort((left, right) => left.timeMs - right.timeMs)
);

export type WegoMotionLibrary = { motions: WegoMotion[] };

export const DEFAULT_FPS = 12;
export const STANDBY_TRANSITION_DURATION_MS = 600;
export const MOTION_LIBRARY_URL = '/motion/animation-page-motions.json';
export const WEGO_MODEL_BASE = '/models/wego';
export const WEGO_URDF_BASE = `${WEGO_MODEL_BASE}/urdf`;
export const WEGO_URDF_URL = `${WEGO_URDF_BASE}/wego.urdf`;
export const WEGO_MESH_BASE = `${WEGO_MODEL_BASE}/meshes`;

const jointEntries = Object.entries(WEGO_JOINTS);
const moveTrackIds: MoveTrackId[] = ['x', 'y', 'z', 'yaw', 'bodyRotationX', 'bodyRotationY', 'bodyRotationZ'];

export const resolveWegoPackagePath = (packageName: string) => {
  if (!packageName || packageName === 'packageName' || packageName === 'meshes') return WEGO_MESH_BASE;
  if (packageName === 'wego' || packageName === 'wego_description') return WEGO_MODEL_BASE;
  if (packageName === 'urdf') return WEGO_URDF_BASE;
  return `${WEGO_MODEL_BASE}/${packageName}`;
};

export const normalizeWegoAssetUrl = (url: string) => url
  .trim()
  .replace(/^package:\/\/([^/]+)\//, (_match, packageName: string) => `${resolveWegoPackagePath(packageName)}/`)
  .replace(/^packageName:\/\//, `${WEGO_MESH_BASE}/`)
  .replace(/^meshes:\/\//, `${WEGO_MESH_BASE}/`)
  .replace(`${WEGO_URDF_BASE}/../meshes/`, `${WEGO_MESH_BASE}/`)
  .replaceAll('/meshes/meshes/', '/meshes/');

export const createStandbyTimeline = (): TimelineData => ({
  name: 'Filo standby',
  fps: DEFAULT_FPS,
  totalFrames: 1,
  joints: jointEntries.map(([id, joint]) => ({
    id,
    label: joint.label,
    color: joint.color,
    keyframes: [{ frame: 0, value: joint.default }],
  })),
  movement: [
    { id: 'x', label: 'Move X', unit: 'm', color: '#0f766e', keyframes: [{ frame: 0, value: 0 }] },
    { id: 'y', label: 'Move Y', unit: 'm', color: '#16a34a', keyframes: [] },
    { id: 'z', label: 'Move Z', unit: 'm', color: '#2563eb', keyframes: [{ frame: 0, value: 0 }] },
    { id: 'yaw', label: 'Turn', unit: 'deg', color: '#dc2626', keyframes: [{ frame: 0, value: 0 }] },
    { id: 'bodyRotationX', label: 'Body X', unit: 'deg', color: '#f59e0b', keyframes: [{ frame: 0, value: 0 }] },
    { id: 'bodyRotationY', label: 'Body Y', unit: 'deg', color: '#0891b2', keyframes: [{ frame: 0, value: 0 }] },
    { id: 'bodyRotationZ', label: 'Body Z', unit: 'deg', color: '#a855f7', keyframes: [{ frame: 0, value: 0 }] },
  ],
  eyeLedKeyframes: [],
});

const valueAtFrame = (keyframes: Keyframe[], frame: number) => (
  interpolate([...keyframes].sort((left, right) => left.frame - right.frame), frame)
);

const nearestEquivalentAngle = (target: number, current: number) => (
  target + Math.round((current - target) / 360) * 360
);

export const createStandbyTransitionTimeline = (
  source: TimelineData,
  durationMs = STANDBY_TRANSITION_DURATION_MS,
): TimelineData => {
  const standby = createStandbyTimeline();
  const totalFrames = Math.max(1, Math.round(Math.max(0, durationMs) / 1000 * source.fps));
  const sourceJoints = new Map(source.joints.map((track) => [track.id, track]));
  const sourceMovement = new Map(source.movement.map((track) => [track.id, track]));

  return {
    name: `${source.name} to standby`,
    fps: source.fps,
    totalFrames,
    joints: standby.joints.map((target) => {
      const sourceTrack = sourceJoints.get(target.id);
      const startValue = sourceTrack?.keyframes.length
        ? valueAtFrame(sourceTrack.keyframes, source.totalFrames)
        : target.keyframes[0].value;
      return {
        ...target,
        keyframes: [
          { frame: 0, value: startValue },
          { frame: totalFrames, value: target.keyframes[0].value },
        ],
      };
    }),
    movement: standby.movement.map((target) => {
      if (source.nativeWalking && target.id === 'y') return { ...target, keyframes: [] };
      const sourceTrack = sourceMovement.get(target.id);
      const startValue = sourceTrack?.keyframes.length
        ? valueAtFrame(sourceTrack.keyframes, source.totalFrames)
        : 0;
      const targetValue = target.id.startsWith('bodyRotation')
        ? nearestEquivalentAngle(target.keyframes[0]?.value ?? 0, startValue)
        : startValue;
      return {
        ...target,
        keyframes: [
          { frame: 0, value: startValue },
          { frame: totalFrames, value: targetValue },
        ],
      };
    }),
    eyeLedKeyframes: [],
  };
};

/**
 * keyframes は frame 昇順であること。compileTimeline が保証する。
 */
export const interpolate = (keyframes: Keyframe[], frame: number) => {
  if (keyframes.length === 0) return 0;
  const first = keyframes[0];
  const last = keyframes[keyframes.length - 1];
  if (frame <= first.frame) return first.value;
  if (frame >= last.frame) return last.value;
  let low = 0;
  let high = keyframes.length - 1;
  while (high - low > 1) {
    const middle = (low + high) >> 1;
    if (keyframes[middle].frame >= frame) high = middle;
    else low = middle;
  }
  const previous = keyframes[low];
  const next = keyframes[high];
  const progress = (frame - previous.frame) / Math.max(1, next.frame - previous.frame);
  return previous.value + (next.value - previous.value) * progress;
};

export const interpolateFromOrigin = (keyframes: Keyframe[], frame: number, origin: number) => (
  origin + interpolate(keyframes, frame) - interpolate(keyframes, 0)
);

const byFrame = (left: { frame: number }, right: { frame: number }) => left.frame - right.frame;

export type CompiledTimeline = {
  data: TimelineData;
  joints: { id: string; keyframes: Keyframe[] }[];
  movement: Record<MoveTrackId, Keyframe[]>;
  eyeLedKeyframes: EyeLedKeyframe[];
};

/** interpolate が要求する「frame 昇順」を保証した参照用構造をつくる。タイムライン切替時に1回だけ呼ぶ。 */
export const compileTimeline = (timeline: TimelineData): CompiledTimeline => ({
  data: timeline,
  joints: timeline.joints.map((track) => ({
    id: track.id,
    keyframes: [...track.keyframes].sort(byFrame),
  })),
  movement: Object.fromEntries(moveTrackIds.map((id) => [
    id,
    [...(timeline.movement.find((track) => track.id === id)?.keyframes ?? [])].sort(byFrame),
  ])) as Record<MoveTrackId, Keyframe[]>,
  eyeLedKeyframes: [...timeline.eyeLedKeyframes].sort(byFrame),
});

const byte = (value: number) => Math.min(255, Math.max(0, Math.round(Number(value) || 0)));
const ledColor = (led: NonNullable<WegoMotionFrame['led']>) => {
  const red = byte(Number(led.r));
  const green = byte(Number(led.g));
  const blue = byte(Number(led.b));
  // The firmware's white-eye preset is stored as this legacy RGB triplet.
  if (red === 155 && green === 55 && blue === 44) return '#ffffff';
  return `#${red.toString(16).padStart(2, '0')}${green.toString(16).padStart(2, '0')}${blue.toString(16).padStart(2, '0')}`;
};

export const getMotionDurationMs = (motion: WegoMotion) => motion.frames.reduce(
  (total, frame) => total + Math.max(0, Number(frame.durationMs) || 0),
  0,
);

export const getTimelineDurationMs = (timeline: TimelineData) => (
  (Math.max(1, timeline.totalFrames) / Math.max(1, timeline.fps)) * 1000
);

export const getTimelineFrameAtElapsed = (timeline: TimelineData, elapsedMs: number, durationMs: number) => {
  if (durationMs <= 0) return timeline.totalFrames;
  const progress = Math.min(1, Math.max(0, elapsedMs / durationMs));
  return Math.round(progress * timeline.totalFrames);
};

export const getSoundKeyframesBetween = (
  keyframes: SoundKeyframe[] | undefined,
  previousFrame: number,
  currentFrame: number,
): SoundKeyframe[] => (keyframes ?? [])
  .filter(({ frame }) => frame > previousFrame && frame <= currentFrame)
  .sort(byFrame);

export const motionToTimeline = (motion: WegoMotion, fps = DEFAULT_FPS): TimelineData => {
  const standby = createStandbyTimeline();
  const jointKeyframes = new Map<string, Keyframe[]>(jointEntries.map(([id]) => [id, []]));
  const movementKeyframes = new Map<MoveTrackId, Keyframe[]>(moveTrackIds.map((id) => [id, []]));
  const eyeLedKeyframes: EyeLedKeyframe[] = [];
  let elapsedMs = 0;

  motion.frames.forEach((motionFrame) => {
    const frameStartMs = elapsedMs;
    elapsedMs += Math.max(0, Number(motionFrame.durationMs) || 0);
    const timelineFrame = Math.round((elapsedMs / 1000) * fps);
    Object.entries(motionFrame.jointsDegrees ?? {}).forEach(([jointId, value]) => {
      if (jointKeyframes.has(jointId) && Number.isFinite(Number(value))) {
        jointKeyframes.get(jointId)!.push({ frame: timelineFrame, value: Number(value) });
      }
    });
    moveTrackIds.forEach((trackId) => {
      const value = Number(motionFrame.movement?.[trackId]);
      if (Number.isFinite(value)) movementKeyframes.get(trackId)!.push({ frame: timelineFrame, value });
    });
    if (motionFrame.led && [motionFrame.led.r, motionFrame.led.g, motionFrame.led.b].every((value) => Number.isFinite(Number(value)))) {
      const ledFrame = [259, 264, 267].includes(motion.motionNo) ? Math.round((frameStartMs / 1000) * fps) : timelineFrame;
      eyeLedKeyframes.push({ frame: ledFrame, color: ledColor(motionFrame.led) });
    }
  });

  const totalFrames = Math.max(1, Math.round((getMotionDurationMs(motion) / 1000) * fps));
  if (motion.nativePlayback?.ledCues.length) {
    eyeLedKeyframes.length = 0;
    motion.nativePlayback.ledCues.forEach(({ timeMs, led }) => {
      eyeLedKeyframes.push({ frame: Math.round(timeMs / 1000 * fps), color: ledColor(led) });
    });
  }
  return {
    name: motion.name,
    fps,
    totalFrames,
    joints: standby.joints.map((track) => ({
      ...track,
      keyframes: jointKeyframes.get(track.id)?.length ? jointKeyframes.get(track.id)! : track.keyframes,
    })),
    movement: standby.movement.map((track) => ({
      ...track,
      keyframes: movementKeyframes.get(track.id)?.length ? movementKeyframes.get(track.id)! : track.keyframes,
    })),
    eyeLedKeyframes,
  };
};

export const parseMotionLibrary = (value: unknown): WegoMotion[] => {
  if (!value || typeof value !== 'object' || !Array.isArray((value as Partial<WegoMotionLibrary>).motions)) {
    throw new Error('モーションライブラリの形式が不正です');
  }
  return (value as WegoMotionLibrary).motions.filter((motion) => (
    Number.isFinite(motion?.motionNo)
    && typeof motion?.name === 'string'
    && Array.isArray(motion?.frames)
    && motion.frames.length > 0
  ));
};
